import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {
    ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECMember,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMemberPersonalData,
    ECMemberPOD,
    ECMemberState,
    ECPodCreationType,
    ECPodUtilityType,
    ECSubjectType,
    EnergyCommunity,
    EnergyCommunityNamedPod,
    ENTITY_METADATA,
    EntityType,
    NewECMemberRequest,
    PlantStatisticalProfile,
    Pod,
    PodStatisticalProfile,
    SpatialCoordinates,
    UpdateECMemberRequest
} from 'smag-commons/cfg/hierarchy';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {catchError, debounceTime, filter, first, map, pairwise, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {SmagApiService} from '../../../api/smag-api.service';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {BooleanResponse, FeaturesConfig, Permission, ProducerPodStatisticalProfileRequest} from 'smag-commons/dto/api';
import {productionPODValidator, validateNotBlank, validatePOD} from '../../../commons/validators';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {ConfigurationService} from '../../../shared/configuration.service';
import {
    DOCUMENT_TYPE_PATTERN,
    EMAIL_PATTERN,
    IBAN_PATTERN,
    MOBILE_PATTERN,
    NAME_PATTERN,
    TAX_ID_PATTERN,
    VAT_NUMBER_PATTERN
} from '../../../shared/patterns';
import {
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    IBAN_MAX_CHARS,
    MOBILE_MAX_CHARS,
    NAME_MAX_CHARS,
    POD_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../../shared/limits';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {I18NService} from '../../../i18n/i18n.service';
import {Address} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {ECMemberAddress, ECMemberDocument, EnergyCommunityNamedPlant} from 'smag-commons/cfg/hierarchy/ec';
import {MsgBoxService} from '../../../shared/msg-box.service';
import * as uuid from 'uuid';
import {VoltageLevel} from 'smag-commons/cfg/voltage-level';
import {Phases, RateMode} from 'smag-commons/cfg';
import {ENTITY_NAME_VALIDATION_PATTERN} from '../../../shared/entity-names';
import {
    ResetContext as StatisticalProfileResetContext,
    StatisticalProfilePlantResource,
    StatisticalProfilePodResourceType,
    StatisticalProfileResourceType
} from 'src/app/shared/statistical-profile/statistical-profile.component';
import {StatisticalProfilePodResource} from '../../../shared/statistical-profile/statistical-profile.component';
import {ResetContext as PredictiveModelResetContext} from '../../../shared/predictive-model/predictive-model.component';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {WizardPodCardMode} from 'src/app/shared/wizard-pod-card/wizard-pod-card.component';
import {errorCode, getContractualPowerValues, isBlank, isDefined} from '../../../commons/commons';
import {MatHorizontalStepper} from '@angular/material/stepper';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {ErrorCode} from '../../../api/errors';
import * as _ from 'lodash';
import {
    getEntitySubtype,
    getFormControl,
    getIncentiveTypes,
    getNominalPower,
    getPlantType,
    getPodStatisticalProfileRequestPlantType,
    getPodUtilityTypes,
    getStatisticalProfileResourceType,
    hasDate,
    isControlOK,
    mkMemberName,
    mkPredictiveModelPlantResource,
    mkStatisticalProfilePlantResource,
    mkTextField,
    PLANT_TYPES,
    PlantType,
    POD_PHASES,
    POD_TYPES,
    POD_VOLTAGES,
    PodEnergyProfiles,
    PV_PLANT_TYPE,
    WIND_PLANT_TYPE
} from '../../../shared/energy-community';
import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {MatSelectChange} from '@angular/material/select';
import {can} from '../../../shared/permissions';
import {Member} from '../ec-members.component';
import {isProducerEntity} from 'smag-commons/fcn';
import {CAPITAL_GRANT_MAX, CAPITAL_GRANT_MIN, limitDecimals, UPPER_INCENTIVE_BOUNDARY} from '../../../shared/ec-economics';
import {addressToString, mkAddress} from '../../../shared/ec-members';
import {getRateMode} from '../../../shared/statistical-profiles';

interface EditedDocument {
    documentId?: string; // in case we are overwriting an already existing document
    documentType: string;
    metadata?: object; // used for some types of documents
    expiration?: number;
    file?: File; // uploaded file
}

const JURIDICAL_TYPES: ECJuridicalType[] = [
    ECJuridicalType.INDIVIDUAL,
    ECJuridicalType.CORPORATE_ENTITY
];

const LEGAL_FORM_TYPES: ECLegalForm[] = [
    ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE,
    ECLegalForm.OTHER_INDIVIDUALS
];

const SUBJECT_TYPES: ECSubjectType[] = [
    ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY,
    ECSubjectType.PMI,
    ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW,
    ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS,
    ECSubjectType.OTHER,
    // ECSubjectType.COMMUNITY  // unnecessary with members
];

const ENTITY_SUBTYPES: ECEntitySubtype[] = [
    ECEntitySubtype.STATE,
    ECEntitySubtype.REGION,
    ECEntitySubtype.PROVINCE,
    ECEntitySubtype.MUNICIPALITY,
    ECEntitySubtype.MUNICIPALITIES_UNION,
    ECEntitySubtype.MOUNTAIN_COMMUNITY,
    ECEntitySubtype.ISLAND_COMMUNITY,
    ECEntitySubtype.METROPOLITAN_CITY,
    ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION,
    ECEntitySubtype.RELIGIOUS_ORGANIZATION,
    ECEntitySubtype.THIRD_SECTOR_ORGANIZATION,
    ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION,
    ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION
];

const ACCESS_TYPES: ECMemberAccessType[] = [
    ECMemberAccessType.MOBILE,
    ECMemberAccessType.ADVANCED,
    ECMemberAccessType.NONE
];

const PARTICIPATION_TYPES: ECMemberParticipationType[] = [
    ECMemberParticipationType.MEMBER,
    ECMemberParticipationType.THIRD_PARTY_PRODUCER
];

interface Document {
    name: string;
    documentId?: string; // only defined if already existing before the current session
    file?: File; // only defined if uploaded during the current session
}

interface MimeType {
    name: string;
    type: string;
}

const DEFAULT_JURIDICAL_TYPE = ECJuridicalType.INDIVIDUAL;
const POD_DEBOUNCE_MILLIS = 250;
const DIALOG_WIDTH = 900;
const MAX_FILE_MB = 2;
const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];
const TYPES_DEBOUNCE_MILLIS = 250;

export interface FormCommunity {
    community: EnergyCommunity;
    communityData: HierarchyEntity;
}

const INTERNAL_DOCUMENT_PREFIX = 'internal_';
export const EC_DOCUMENT_TYPE_OTHER = 'Other';

@Component({
    selector: 'app-new-ec-member',
    templateUrl: './new-ec-member.component.html',
    styleUrls: ['./new-ec-member.component.scss']
})
export class NewECMemberComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private timeZone: string = null;
    private currentAddress: ECMemberAddress = null;
    private currentCoordinates: SpatialCoordinates = null;
    private featureConfig: FeaturesConfig;
    private readonly documents: { [id: string]: Document } = {};

    @ViewChild('deletePod') private deletePodDialog: TemplateRef<any>;
    @ViewChild('setEffectiveDate') private setEffectiveDateDialog: TemplateRef<any>;
    @ViewChild('setPlantDate') private setPlantDateDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('adviceDialog') private adviceDialog: TemplateRef<any>;
    @ViewChild('setDeactivationDate') private setDeactivationDateDialog: TemplateRef<any>;

    @Input() community: FormCommunity;
    @Input() member: Member;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

    ecMember: ECMember;
    form1: FormGroup;
    form2: FormGroup;
    step3Enabled: boolean;
    step4Enabled: boolean;
    step3Busy = false;
    window = window;
    documentTypes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    accessTypeFormControl: FormControl = new FormControl(null, []);
    participationTypes: ECMemberParticipationType[] = PARTICIPATION_TYPES;
    accessTypes: ECMemberAccessType[] = ACCESS_TYPES;

    readonly MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    readonly EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    readonly VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    readonly IBAN_MAX_CHARS = IBAN_MAX_CHARS;
    readonly ACCEPTED_MIME_TYPES = FILE_MIME_TYPES.map(mt => mt.type).join(',');
    readonly MAX_FILE_MB: number = MAX_FILE_MB;
    readonly DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    readonly POD_TYPES = POD_TYPES;
    readonly NAME_MAX_CHARS = NAME_MAX_CHARS;
    readonly UPPER_INCENTIVE_BOUNDARY = UPPER_INCENTIVE_BOUNDARY;
    readonly PLANT_TYPES = PLANT_TYPES;
    readonly EC_DOCUMENT_TYPE_OTHER = EC_DOCUMENT_TYPE_OTHER;
    readonly JURIDICAL_TYPES = JURIDICAL_TYPES;
    readonly LEGAL_FORM_TYPES = LEGAL_FORM_TYPES;
    readonly SUBJECT_TYPES = SUBJECT_TYPES;
    readonly ENTITY_SUBTYPES = ENTITY_SUBTYPES;
    readonly POD_PHASES = POD_PHASES;
    readonly POD_VOLTAGES = POD_VOLTAGES;
    readonly WIND_PLANT_TYPE = WIND_PLANT_TYPE;
    readonly ECJuridicalType = ECJuridicalType;
    readonly ECSubjectType = ECSubjectType;
    readonly PodEnergyProfiles = PodEnergyProfiles;
    readonly WizardPodCardMode = WizardPodCardMode;
    readonly contractualPowerValues = getContractualPowerValues();
    readonly getFormControl = getFormControl;
    readonly getPodUtilityTypes = getPodUtilityTypes;
    readonly limitDecimals = limitDecimals;
    readonly getIncentiveTypes = getIncentiveTypes;
    readonly hasDate = hasDate;
    readonly mkPredictiveModelPlantResource = mkPredictiveModelPlantResource;
    readonly mkStatisticalProfilePlantResource = mkStatisticalProfilePlantResource;
    readonly isControlOK = isControlOK;

    constructor(private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly hierarchyService: HierarchyService,
                private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private fb: FormBuilder) {
        this.form1 = this.fb.group({
            participationType: new FormControl(null, [Validators.required]),
            juridicalType: new FormControl(DEFAULT_JURIDICAL_TYPE, [Validators.required]),
            legalForm: new FormControl(null, []),
            subjectType: new FormControl(null, []),
            entitySubtype: new FormControl(null, []),
            firstName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            lastName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            companyName: new FormControl(null, []),
            email: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)]),
            pec: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)]),
            confirmEmail: new FormControl(null, []),
            mobileNumber: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            phoneNumber: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            fiscalCode: new FormControl(null, [Validators.pattern(TAX_ID_PATTERN)]),
            vatNumber: new FormControl(null, [Validators.pattern(VAT_NUMBER_PATTERN)]),
            iban: new FormControl(null, [Validators.pattern(IBAN_PATTERN)]),
            address: new FormControl(null, [Validators.required]),
            documents: this.fb.array([]),
            // shown in the third wizard step
            sendInvitation: new FormControl(false, [Validators.required])
        }, {
            validators: [validateForm]
        });
        this.form2 = new FormGroup({pods: new FormArray([])});
    }

    ngOnInit() {
        this.ecMember = this.member?.member;

        this.configurationService.timeConfig
            .pipe(takeUntil(this.destroyed))
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.apiService.getFeatureConfig()
            .pipe(takeUntil(this.destroyed))
            .subscribe(featureConfig => {
                this.featureConfig = featureConfig;
                this.participationTypes = this.featureConfig.third_party_producer ? PARTICIPATION_TYPES : [ECMemberParticipationType.MEMBER];
                const participationType = this.ecMember && this.ecMember.participation_type;
                if (this.member && this.member.member.participation_type !== ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
                    forkJoin(this.member.pods.map(pod => this.apiService.findEntityByPOD(pod.pod.pod)
                        .pipe(switchMap(resp => this.apiService.getECPod(this.community.communityData.id, resp.id)),
                            map(ecNamedPod => isProducerEntity(pod.resource.entity, ecNamedPod.pod)))))
                        .subscribe(isProducer =>
                            this.setAccessTypes(isProducer.some(Boolean) ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null));
                } else {
                    this.setAccessTypes(participationType);
                }
            });
        this.apiService.getCustomerConfig(this.community.communityData.id).subscribe(customerCfg =>
            this.documentTypes.next([EC_DOCUMENT_TYPE_OTHER].concat(customerCfg.subject_documents?.map(document => document.name) || [])));
        this.internalInit();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get isStep2Valid(): boolean {
        let valid = true;
        this.podControls.forEach(podForm => {
            if (valid) {
                const existingResource = this.hasExistingResource(podForm);
                let checkProducerPods = true;
                // Checks only if there is a POD with no subject assigned
                if (!!this.podEntity(podForm)) {
                    // Mobile app members cannot have producer pods
                    if (this.isProducerResource(podForm) && this.isMobileMember) {
                        checkProducerPods = false;
                    }
                    // Third party producer cannot have consumer/prosumer pods
                    if (!this.isProducerResource(podForm) && this.isThirdPartyProducer) {
                        checkProducerPods = false;
                    }
                }
                // .dirty is not needed to validate step
                const podFormValid = podForm.controls.podCode.valid &&
                    (podForm.controls.podAddressLikeMember.value ||
                        (!isBlank(podForm.controls.podAddress.value) && podForm.controls.podAddress.valid));
                valid = valid && podFormValid && !existingResource && checkProducerPods;
            }
        });
        return valid && !this.continueDisabled;
    }

    get isCommunityActivationDateEmpty(): boolean {
        return !this.community.community.activation_date;
    }

    get isCompany(): boolean {
        return !(this.juridicalType === ECJuridicalType.INDIVIDUAL && this.legalFormType === ECLegalForm.OTHER_INDIVIDUALS);
    }

    get juridicalType(): ECJuridicalType {
        return this.form1.controls.juridicalType.value;
    }

    get legalFormType(): ECLegalForm {
        return this.form1.controls.legalForm.value;
    }

    get entitySubtype(): ECEntitySubtype {
        return this.form1.controls.entitySubtype.value;
    }

    get subjectType(): ECSubjectType {
        return this.form1.controls.subjectType.value;
    }

    get participationType(): ECMemberParticipationType {
        return this.form1.controls.participationType.value;
    }

    get isThirdPartyProducer() {
        return this.participationType === ECMemberParticipationType.THIRD_PARTY_PRODUCER;
    }

    get isMobileMember() {
        return this.participationType === ECMemberParticipationType.MEMBER && this.getAccessType === ECMemberAccessType.MOBILE;
    }

    get isUpdate(): boolean {
        return !!this.member;
    }

    get invalidCompanyName(): boolean {
        return this.form1.controls.companyName.dirty && this.form1.controls.companyName.invalid;
    }

    get invalidFirstName(): boolean {
        return this.form1.controls.firstName.dirty && this.form1.controls.firstName.invalid;
    }

    get invalidLastName(): boolean {
        return this.form1.controls.lastName.dirty && this.form1.controls.lastName.invalid;
    }

    get invalidEmail(): boolean {
        return this.form1.controls.email.dirty && this.form1.controls.email.invalid;
    }

    get uniqueEmailError(): boolean {
        return this.form1.controls.email.hasError('unique_email');
    }

    get invalidConfirmEmail(): boolean {
        return this.form1.controls.confirmEmail.dirty && this.form1.controls.confirmEmail.invalid;
    }

    get invalidMobileNumber(): boolean {
        return this.form1.controls.mobileNumber.dirty && this.form1.controls.mobileNumber.invalid;
    }

    get notMatchingEmails(): boolean {
        return this.form1.controls.confirmEmail.dirty && this.form1.controls.confirmEmail.hasError('not_matching_emails');
    }

    get communityActivationDate(): Moment {
        return this.community && this.community.community ? moment.tz(this.community.community.activation_date, this.timeZone) : undefined;
    }

    get addressFormControl(): FormControl {
        return this.form1.controls.address as FormControl;
    }

    get getAccessType(): ECMemberAccessType {
        return this.accessTypeFormControl.value || this.ecMember?.access_type;
    }

    get firstName(): string {
        return this.form1.controls.firstName.value;
    }

    get companyName(): string {
        return this.form1.controls.companyName.value;
    }

    get lastName(): string {
        return this.form1.controls.lastName.value;
    }

    get continueDisabled(): boolean {
        return this.documentControls.some(fg => this.uploadedFileName(fg) === null);
    }

    get showInvitingForm(): boolean {
        return !this.isUpdate ||
            this.ecMember.state === ECMemberState.NOT_INVITED ||
            this.ecMember.state === ECMemberState.PENDING ||
            this.ecMember.state === ECMemberState.CANCELED;
    }

    get enableInvitationInput(): boolean {
        const email = this.form1.controls.email;
        const confirmEmail = this.form1.controls.confirmEmail;
        const mobile = this.form1.controls.mobileNumber;
        const sendInvitation = this.form1.controls.sendInvitation;
        if (email.value && confirmEmail.value && mobile.value && email.valid && confirmEmail.valid && mobile.valid) {
            return true;
        }
        sendInvitation.setValue(false);
        return false;
    }

    get documentsFormArray(): FormArray {
        return this.form1.controls.documents as FormArray;
    }

    get documentControls(): FormGroup[] {
        return (this.documentsFormArray).controls as FormGroup[];
    }

    get mimeTypesNames(): string {
        return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
    }

    get mimeTypes(): string[] {
        return FILE_MIME_TYPES.map(mt => mt.type);
    }

    get podsFormArray(): FormArray {
        return this.form2.controls.pods as FormArray;
    }

    get podControls(): FormGroup[] {
        return (this.podsFormArray).controls as FormGroup[];
    }

    get isNotInvited(): boolean {
        if (this.isUpdate) {
            return this.ecMember.state === ECMemberState.NOT_INVITED;
        }
        return false;
    }

    get isPending() {
        if (this.isUpdate) {
            return this.ecMember.state === ECMemberState.PENDING;
        }
        return false;
    }

    get duplicatePodValidator(): ValidatorFn {
        const scope = this;
        return (currentCtrl: AbstractControl): ValidationErrors => {
            const currentValue = currentCtrl.value;

            if (typeof currentValue !== 'string' || isBlank(currentValue)) { // allow optional control
                return null;
            }

            let error = null;
            for (const fg of scope.podControls) {
                const ctrl = fg.controls.podCode;
                if (ctrl !== currentCtrl) {
                    const ctrlValue = ctrl.value;
                    if ((typeof ctrlValue === 'string') && !isBlank(ctrlValue)) {
                        if (currentValue === ctrlValue) {
                            // error is shown only on one form
                            // ctrl.setErrors({unique_pod: true});
                            error = {unique_pod: true} as ValidationErrors;
                        }
                    }
                }
            }
            return error;
        };
    }

    isPersistentPod(podForm: FormGroup): boolean {
        return !!podForm.controls.persistentPod.value as boolean;
    }

    podEntity(podForm: FormGroup): HierarchyEntity {
        return podForm.controls.podEntity.value as HierarchyEntity;
    }

    hasEntity(podForm: FormGroup): boolean {
        return !!podForm.controls.entityName.value as boolean;
    }

    podError(fg: FormGroup): string {
        let podErr = null;
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError('unique_pod')) {
            podErr = 'unique_pod';
        }
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError('pod_has_capital_grant')) {
            podErr = 'pod_has_capital_grant';
        }
        if (fg.controls.podCode.dirty && (fg.controls.podCode.hasError('pod') || fg.controls.podCode.hasError('empty'))) {
            podErr = 'pod';
        }
        let dateErr = null;
        if (fg.controls.podActivationDate.dirty && fg.controls.podActivationDate.invalid) {
            dateErr = 'bad_date';
        }
        return podErr || dateErr;
    }

    plantPodError(fg: FormGroup): string {
        let podErr = null;
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError('unique_pod')) {
            podErr = 'unique_pod';
        }
        if (fg.controls.podCode.dirty && (fg.controls.podCode.hasError('pod') || fg.controls.podCode.hasError('empty'))) {
            podErr = 'pod';
        }
        return podErr;
    }

    getControl(form: FormGroup, name: string): FormControl {
        return form.controls[name] as FormControl;
    }

    addressChange(event: Address | null) {
        this.currentAddress = mkAddress(event);
        this.currentCoordinates = event ? event.coordinates : null;
        this.addressFormControl.setValue(this.currentAddress);
    }

    onClickAddDocument() {
        // const id = Math.max(this.documentControls.map(fg => fg.controls.id.value as number)) + 1;
        const formUuid = INTERNAL_DOCUMENT_PREFIX + uuid.v4(); // internal document id
        const documentForm = this.fb.group({
            type: new FormControl('', [Validators.required]),
            customType: new FormControl('', [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
            id: new FormControl(formUuid, null),
            fileName: new FormControl(null, null)
        }, {
            validators: [validateDocumentType]
        });
        this.documentsFormArray.push(documentForm);
    }

    uploadedFileName(fc: FormGroup) {
        return fc.controls.fileName.value;
    }

    onUploadDocumentFile(documentForm: FormGroup, event: Event) {
        const files = 'files';
        const id = documentForm.controls.id.value as string;
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                documentForm.controls.fileName.setValue(file.name);
                this.documents[id] = {
                    name: file.name,
                    documentId: id,
                    file
                };
            }
        } else {
            documentForm.controls.fileName.setValue(null);
        }
        this.updateForm(documentForm);
    }

    hasDocFileError(fg: FormGroup): boolean {
        return fg.hasError('missing_file');
    }

    onClickRemoveDocument(index: number, documentForm: FormGroup) {
        const id = documentForm.controls.id.value;
        this.documents[id] = null;
        this.documentsFormArray.removeAt(index);
    }

    cancel() {
        this.closeDialog.emit(false);
    }

    detectSelectChanges($event: MatSelectChange) {
        if (!this.isUpdate) {
            const value = $event.value as ECMemberParticipationType;
            this.setAccessTypes(value);
        }
    }

    /**
     * Manage selection stepper changes from header
     */
    selectionChange(selectionEvent: StepperSelectionEvent) {
        if (selectionEvent.selectedIndex === 2) {
            // like  gotoStep3
            this.changeEnergyProfile();
            this.mkPromises().then();
            this.disableUtilityType();
        }
    }

    canResetActivationDate(podForm: FormGroup) {
        const currentPod = podForm.controls.podCode.value;
        const pod = this.ecMember.pods.find((ecMemberPod) => ecMemberPod.pod === currentPod);
        if (pod) {
            return !pod.effective_date;
        }
        return true;
    }

    podAddressFormControl(fg: FormGroup): FormControl {
        return fg.controls.podAddress as FormControl;
    }

    onClickAddPod() {
        const podForm = this.fb.group({
            podCode: new FormControl(null, [validateNotBlank, validatePOD, this.duplicatePodValidator], [ctrl => this.validatePOD(ctrl)]),
            podAddressLikeMember: new FormControl(false, []),
            podAddress: new FormControl('', [Validators.required]),
            aspiringProsumer: new FormControl(null, []),
            // for internal use only
            internalPodAddress: new FormControl(null, []),
            // for internal use only
            internalPodCoordinates: new FormControl(null, []),
            hasWifi: new FormControl(false, []),
            podProvider: new FormControl('', []),
            podActivationDate: new FormControl('', []),
            podDeactivationDate: new FormControl('', []),
            creationType: new FormControl(ECPodCreationType.MANUAL, []),
            // third step
            podEnergyProfile: new FormControl(this.isThirdPartyProducer ? PodEnergyProfiles.PRODUCER : PodEnergyProfiles.CONSUMER, [Validators.required]),
            podUtilityType: new FormControl('', [Validators.required]),
            podVoltage: new FormControl(VoltageLevel.LOW, [Validators.required]),
            podPhase: new FormControl(Phases.SINGLE, [Validators.required]),
            contractualPower: new FormControl('', []),

            plants: new FormArray([]),
            // NB: if entityName is filled, energy parameters are not editable
            entityName: new FormControl(null, []),
            podEntity: new FormControl(null, []),
            podEntityEnergyProfile: new FormControl(null, []),
            podExists: new FormControl(null, []),
            persistentPod: new FormControl(false, []),
            predictiveModel: new FormControl(null, []),

            // fourth step
            profile: new FormControl(null, []) // statistical profile
        }, {
            //  validator: this.userAddressValidator
        });
        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                if (!this.podEntity(podForm)) {
                    if (value === PodEnergyProfiles.PRODUCER || value === PodEnergyProfiles.PROSUMER) {
                        if (this.plantsFormArray(podForm).length === 0) {
                            this.onClickAddPlant(podForm);
                        }
                    } else {
                        this.plantsFormArray(podForm).clear();
                    }
                    if (value === PodEnergyProfiles.PRODUCER) {
                        podForm.controls.podUtilityType.setValue('');
                        podForm.controls.podUtilityType.disable();
                    } else {
                        podForm.controls.podUtilityType.enable();
                        podForm.controls.podUtilityType.setValidators(Validators.required);
                    }
                    this.setAccessTypesByEnergyProfiles();
                }
            });
        this.mkPODEntityObs(podForm.controls.podCode.valueChanges
            .pipe(startWith(null as HierarchyEntity),
                takeUntil(this.destroyed),
                tap(() => {
                    podForm.controls.podEntity.setValue(null);
                    podForm.controls.podExists.setValue(null);
                    podForm.controls.podEntityEnergyProfile.setValue(null);
                })))
            .subscribe(entity => {
                if (!!entity) {
                    if (isHierarchyEntity(entity)) {
                        this.apiService.getECPod(entity.parent.id, entity.id).toPromise()
                            .then((namedPod) => {
                                const profile = getProfile(namedPod);
                                podForm.controls.podEntityEnergyProfile.setValue(profile);
                                podForm.controls.podEntity.setValue(entity ? entity : null);
                            });
                    }
                    if (isBooleanResponse(entity)) {
                        podForm.controls.podExists.setValue(entity ? entity : null);
                    }
                } else {
                    podForm.controls.podEntity.setValue(null);
                    podForm.controls.podExists.setValue(null);
                    podForm.controls.podEntityEnergyProfile.setValue(null);
                }
            });
        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this.destroyed))
            .pipe(startWith(undefined as string), pairwise())
            .subscribe((values: string[]) => {
                if (values[0] !== values[1]) {
                    this.resetPodStatisticalProfile(podForm);
                }
            });
        this.managePodAddressLikeMemberChange(podForm);
        this.podsFormArray.push(podForm);
        // todo evaluate to use value change in podsFormArray
        this.refreshSteps3Enabled();
        this.accessTypeFormControl.reset();
        this.setAccessTypesByEnergyProfiles();
    }

    hasExistingResource(podForm: FormGroup) {
        return !this.podEntity(podForm) && !!this.podExists(podForm) && this.podExists(podForm).value;
    }

    isProducerResource(podForm: FormGroup) {
        return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.PRODUCER;
    }

    onClickRemovePod(index: number) {
        const podForm = this.podsFormArray.at(index) as FormGroup;
        if (this.isPersistentPod(podForm)) {
            this.dialogService.open<{ pod: string, memberName: string }, boolean>({
                title: this.i18n.translate('Are you sure you want to delete this POD?'),
                template: this.deletePodDialog,
                context: {
                    pod: podForm.controls.podCode.value,
                    memberName: mkMemberName({
                        personal_data: {
                            first_name: this.form1.controls.firstName.value,
                            last_name: this.form1.controls.lastName.value,
                            company_name: this.form1.controls.companyName.value
                        }
                    })
                }
            }).closed.then(del => {
                if (del) {
                    this.podsFormArray.removeAt(index);
                }
            });
        } else {
            this.podsFormArray.removeAt(index);
        }
        this.refreshSteps3Enabled();
    }

    addressPodChange(event: Address, fg: FormGroup) {
        fg.controls.internalPodAddress.setValue(mkAddress(event));
        fg.controls.internalPodCoordinates.setValue(event.coordinates);
    }

    podHasActivationDate(fg: FormGroup): boolean {
        return !!fg.controls.podActivationDate.value;
    }

    onClickResetPodActivationDate(fg: FormGroup) {
        fg.controls.podActivationDate.setValue(null);
    }

    onClickSetPodActivationDate(fg: FormGroup) {
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD activation date'),
            template: this.setEffectiveDateDialog,
            context: {
                minDate: this.communityActivationDate,
                timezone: this.timeZone
            }
        }, {
            width: DIALOG_WIDTH,
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                fg.controls.podActivationDate.setValue(effectiveDate.startOf('day'));
                fg.controls.podActivationDate.disable();
            }
        });
    }

    onClickSetDeactivationDate(podForm: FormGroup) {
        const minDate = this.podMinDeactivationDate(podForm);
        const today = moment.tz(this.timeZone).startOf('day');
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD deactivation date'),
            template: this.setDeactivationDateDialog,
            context: {
                pod: podForm.controls.podDeactivationDate.value,
                memberName: mkMemberName(this.ecMember),
                minDate,
                date: new FormControl(minDate.isSameOrAfter(today) ?
                    minDate : today, [Validators.required]) // use "now" as a start
            }
        }).closed.then(deactivationDate => {
            if (deactivationDate) {
                podForm.controls.podDeactivationDate.setValue(deactivationDate);
                podForm.controls.podDeactivationDate.disable();
            }
        });
    }

    podMinDeactivationDate(podForm: FormGroup): Moment {
        const effectiveDate: Moment = podForm.controls.podActivationDate.value;
        return effectiveDate ? effectiveDate.clone().add(1, 'days') : this.communityActivationDate.clone().add(1, 'days');
    }

    podHasDeactivationDate(fg: FormGroup): boolean {
        return !!fg.controls.podDeactivationDate.value;
    }

    podDeactivationDateHasError(podForm: FormGroup): boolean {
        const ctrl = podForm.controls.podDeactivationDate;
        return ctrl.invalid;
    }

    podHasError(podForm: FormGroup, error: string): boolean {
        if (error === 'date_mismatch') {
            return podForm.hasError('pod_date_mismatch');
        }
        return false;
    }

    isDeactivationDateSetInStone(podForm: FormGroup) {
        const currentPod = podForm.controls.podCode.value;
        const pod = this.ecMember.pods.find((ecMemberPod) => ecMemberPod.pod === currentPod);
        if (pod) {
            return !!pod.deactivation_date;
        }
        return false;
    }

    onClickResetPodDeactivationDate(fg: FormGroup) {
        fg.controls.podDeactivationDate.setValue(null);
    }

    invalidPodProvider(podForm: FormGroup) {
        return podForm.controls.podProvider.dirty && podForm.controls.podProvider.invalid;

    }

    podEnergyProfile(podForm: FormGroup): PodEnergyProfiles {
        return podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
    }

    mkStatisticalProfilePodResource(podForm: FormGroup): StatisticalProfilePodResource {
        const podCode = podForm.controls.podCode.value;
        const plants: StatisticalProfilePlantResource[] = this.plantControls(podForm).map(plantForm => ({
            type: getStatisticalProfileResourceType(plantForm.controls.plantType.value),
            name: plantForm.controls.plantName.value as string,
            profile: plantForm.controls.profile.value as PlantStatisticalProfile,
            nominalPower: plantForm.controls.nominalPower.value
        }));
        return {
            type: StatisticalProfileResourceType.POD,
            name: podCode,
            podType: mkStatisticalProfilePodType(this.podEnergyProfile(podForm)),
            plants
        };
    }

    mkPodStatisticalProfileResetValueProvider(): (ctx: StatisticalProfileResetContext) => Promise<PodStatisticalProfile> {
        return () => Promise.resolve(null); // reset statistical profile for POD (consumer or prosumer)
    }

    plantControls(podForm: FormGroup): FormGroup[] {
        return this.plantsFormArray(podForm).controls as FormGroup[];
    }

    onClickAddPlant(podForm: FormGroup) {
        const plantCounter = this.plantsFormArray(podForm).length + 1;
        let plantName: string;
        if (this.isCompany) {
            plantName = `imp ${subStringForName(this.companyName, 6, true)} ${plantCounter}`;
        } else {
            plantName = `imp ${subStringForName(this.firstName, 3, false)} ${subStringForName(this.lastName, 3, false)} ${plantCounter}`;
        }
        const plantForm = this.fb.group({
            plantName: new FormControl(plantName, [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)]),
            plantType: new FormControl(PV_PLANT_TYPE, [Validators.required]),
            nominalPower: new FormControl(null, [Validators.required, Validators.min(0)]),
            capitalGrant: new FormControl(0, this.isThirdPartyProducer ? [] : [Validators.required, Validators.min(CAPITAL_GRANT_MIN), Validators.max(CAPITAL_GRANT_MAX)]),
            enterData: new FormControl(null, []),
            incentiveType: new FormControl(null, [Validators.required]),
            profile: new FormControl(null, [Validators.required]),
            predictiveModel: new FormControl(null, []),
            podCode: new FormControl(null, [this.mkPlantPodValidator()], [])
        });
        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this.destroyed),
                startWith(undefined as number),
                pairwise())
            .subscribe((values: number[]) => {
                if (values[0] !== values[1]) {
                    this.resetPodStatisticalProfile(podForm);
                }
            });
        plantForm.controls.enterData.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(plantActivationDate => {
                if (plantActivationDate && plantActivationDate.isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone))) {
                    plantForm.controls.capitalGrant.setValue(0);
                    plantForm.controls.capitalGrant.disable();
                } else {
                    plantForm.controls.capitalGrant.enable();
                }
                getIncentiveTypes(plantForm, true);  // updates types of incentive based on data entered
            });
        this.updatePlantStatAndPredModOnChange(plantForm);
        this.resetPodStatisticalProfile(podForm);
        this.plantsFormArray(podForm).push(plantForm);
    }

    isPlantControlOK(fg: FormGroup, controlName: string) {
        return fg.controls[controlName].dirty && !fg.controls[controlName].valid;
    }

    getPlantDateError(form: FormGroup): string {
        let dateErr = null;
        if (form.controls.enterData.dirty && form.controls.enterData.invalid) {
            dateErr = 'bad_date';
        }
        return dateErr;
    }

    onClickResetPlantDate(plantForm: FormGroup) {
        plantForm.controls.enterData.setValue(null);
    }

    onClickSetPlantDate(plantForm: FormGroup) {
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('Date of commissioning of the plant'),
            template: this.setPlantDateDialog,
            context: {
                timezone: this.timeZone
            }
        }, {
            width: 900
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                plantForm.controls.enterData.setValue(effectiveDate.startOf('day'));
                plantForm.controls.enterData.disable();
            }
        });
    }

    mkPlantStatisticalProfileResetValueProvider(plantForm: FormGroup): (ctx: StatisticalProfileResetContext) => Promise<PlantStatisticalProfile> {
        return ctx => {
            const nominalPower = plantForm.controls.nominalPower.value;
            return this.mkPlantDefaultStatisticalProfile(nominalPower, ctx.rateMode || RateMode.SINGLE, getPlantType(plantForm)).toPromise();
        };
    }

    mkPlantPredictiveModelResetValueProvider(plantForm: FormGroup): (ctx: PredictiveModelResetContext) => Promise<PredictiveModelInfo> {
        return () => this.mkPlantDefaultPredictiveModel(getNominalPower(plantForm), getPlantType(plantForm)).toPromise();
    }

    onClickRemovePlant(podForm: FormGroup, j: number) {
        this.plantsFormArray(podForm).removeAt(j);
        this.plantControls(podForm).forEach(fg => {
            const nameSplit = fg.controls.plantName.value.split(' ');
            const currentNumberPlant = isDefined(nameSplit[nameSplit.length - 1]) ? +nameSplit[nameSplit.length - 1] : 0;
            const newNamePlant = currentNumberPlant < (j + 1) ? currentNumberPlant : currentNumberPlant - 1;
            let plantName: string;
            if (this.isCompany) {
                plantName = `imp ${subStringForName(this.companyName, 6, true)} ${newNamePlant}`;
            } else {
                plantName = `imp ${subStringForName(this.firstName, 3, false)} ${subStringForName(this.lastName, 3, false)} ${newNamePlant}`;
            }
            fg.controls.plantName.setValue(plantName);
        });
        this.resetPodStatisticalProfile(podForm);
    }

    aspiringProsumerForForm(podForm: FormGroup) {
        return podForm.controls.aspiringProsumer.value;
    }

    removeAspiringProsumer(podForm: FormGroup) {
        podForm.controls.aspiringProsumer.setValue(false);
    }

    goToStep3(stepper: MatHorizontalStepper) {
        stepper.next();
    }

    goToStep4(stepper: MatHorizontalStepper) {
        stepper.next();
    }

    save() {
        if (this.isUpdate) {
            this.updateMember().then();
        } else {
            this.createMember().then();
        }
    }

    private podExists(podForm: FormGroup): BooleanResponse {
        return podForm.controls.podExists.value as BooleanResponse;
    }

    private refreshSteps3Enabled() {
        this.step3Enabled = this.podControls.map((podForm: FormGroup) => {
            return !podForm.controls.persistentPod.value as boolean; // true if not persistent
        }).reduce((p, c) => p || c, false);
        this.refreshSteps4Enabled();
    }

    private refreshSteps4Enabled() {
        this.step4Enabled = this.step3Enabled &&
            this.podControls.map(podForm => !podForm.controls.entityName.value as boolean) // true if not persistent
                .reduce((p, c) => p || c, false);
        this.setAccessTypesByEnergyProfiles();
    }

    private internalInit(): void {
        //  *** NEW items ***
        if (!this.isUpdate) {
            this.onClickAddPod(); // add an empty pod
            // manage juridical, subject types, entity subtypes values
            this.form1.controls.juridicalType.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.form1.controls.legalForm.setValue(null);
                    this.form1.controls.subjectType.setValue(null);
                    this.form1.controls.entitySubtype.setValue(null);
                });
            this.form1.controls.subjectType.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.form1.controls.legalForm.setValue(null);  // legal form has no use if we have a subject type
                    this.form1.controls.entitySubtype.setValue(null);
                });
            this.form1.controls.entitySubtype.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.podControls.forEach(podForm => {
                        podForm.controls.podUtilityType.setValue(null);
                    });
                });
        } else {
            this.form1.controls.participationType.setValue(this.ecMember.participation_type);
            this.form1.controls.juridicalType.setValue(this.ecMember.juridical_type);
            this.form1.controls.legalForm.setValue(this.ecMember.legal_form);
            this.form1.controls.subjectType.setValue(this.ecMember.subject_type);
            this.form1.controls.entitySubtype.setValue(this.ecMember.entity_subtype);
            this.form1.controls.firstName.setValue(this.ecMember.personal_data.first_name);
            this.form1.controls.lastName.setValue(this.ecMember.personal_data.last_name);
            this.form1.controls.companyName.setValue(this.ecMember.personal_data.company_name);
            this.form1.controls.email.setValue(this.ecMember.personal_data.email);
            this.form1.controls.pec.setValue(this.ecMember.personal_data.pec);
            this.form1.controls.confirmEmail.setValue(this.ecMember.personal_data.email);
            this.form1.controls.mobileNumber.setValue(this.ecMember.personal_data.mobile_number);
            this.form1.controls.phoneNumber.setValue(this.ecMember.personal_data.telephone_number);
            this.form1.controls.fiscalCode.setValue(this.ecMember.personal_data.cf);
            this.form1.controls.vatNumber.setValue(this.ecMember.personal_data.piva);
            this.form1.controls.iban.setValue(this.ecMember.personal_data.iban);
            this.form1.controls.address.setValue(addressToString(this.ecMember.personal_data.address));
            this.currentAddress = this.ecMember.personal_data.address;
            this.currentCoordinates = this.ecMember.spatial_coordinates;
            this.accessTypeFormControl.setValue(this.ecMember.access_type);
            this.accessTypes = this.ecMember.participation_type === ECMemberParticipationType.THIRD_PARTY_PRODUCER ? [ECMemberAccessType.ADVANCED, ECMemberAccessType.NONE] : ACCESS_TYPES;
            this.documentTypes.pipe(takeUntil(this.destroyed))
                .subscribe(docTypes => {
                    this.ecMember.documents.forEach(doc => {
                            const documentForm = this.fb.group({
                                type: new FormControl(docTypes.includes(doc.document_type) ? doc.document_type : EC_DOCUMENT_TYPE_OTHER, [Validators.required]),
                                customType: new FormControl(docTypes.includes(doc.document_type) ? '' : doc.document_type, [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
                                id: new FormControl(doc.id, null),
                                fileName: new FormControl(doc.filename, null)
                            }, {
                                validators: [validateDocumentType]
                            });
                            this.documentsFormArray.push(documentForm);
                        }
                    );
                });


            this.ecMember.pods.forEach(pod => {
                const podForm = this.fb.group({
                    podId: new FormControl(null, []), // readonly
                    podCode: new FormControl(pod.pod, []), // readonly
                    podAddressLikeMember: new FormControl(false, []),
                    podAddress: new FormControl(addressToString(pod.address), []),
                    aspiringProsumer: new FormControl(pod.aspiring_prosumer, []),
                    // for internal use only
                    internalPodAddress: new FormControl(pod.address, []),
                    // for internal use only
                    internalPodCoordinates: new FormControl(pod.spatial_coordinates, []),
                    hasWifi: new FormControl(pod.has_wifi, []),
                    podProvider: new FormControl(pod.energy_provider, []),
                    podActivationDate: new FormControl(pod.effective_date ? moment(pod.effective_date).startOf('day') : '', []),
                    podDeactivationDate: new FormControl(pod.deactivation_date ? moment(pod.deactivation_date).startOf('day') : '', []),
                    creationType: new FormControl(pod.creation_type, []),
                    // third step
                    podEnergyProfile: new FormControl(PodEnergyProfiles.CONSUMER, []/*[Validators.required]*/),
                    podUtilityType: new FormControl(pod.pod_utility_type, []),
                    podVoltage: new FormControl(VoltageLevel.LOW, []/*[Validators.required]*/),
                    podPhase: new FormControl(Phases.SINGLE, []/*[Validators.required]*/),
                    contractualPower: new FormControl('', []),

                    profile: new FormControl(null, []),

                    plants: new FormArray([]),
                    // NB: if entityName is filled, energy parameters are not editable
                    entityName: new FormControl(null, []),
                    podEntity: new FormControl(null, []),
                    podEntityEnergyProfile: new FormControl(null, []),
                    podExists: new FormControl(null, []),
                    // if true pod is already saved into system
                    persistentPod: new FormControl(true, []),
                    predictiveModel: new FormControl(null, [])
                }, {
                    validator: ctrls => this.validatePodDates(ctrls)
                });
                this.podsFormArray.push(podForm);
                this.managePodAddressLikeMemberChange(podForm);
            });
            this.form1.updateValueAndValidity();
        }
    }

    private mkPODEntityObs(pod: Observable<string>): Observable<HierarchyEntity | BooleanResponse> {
        return combineLatest([this.hierarchyService.hierarchyIds, pod]).pipe(
            debounceTime(POD_DEBOUNCE_MILLIS),
            switchMap(([entityIds, podCode]) => {
                if (!!podCode && !!podCode.trim().length) {
                    const findEntity$ = this.apiService.findEntityByPOD(podCode.trim()).pipe(
                        map(entityId => entityId ? entityIds[entityId.id] : null),
                        map(entity => !!entity && can(entity.permissions, Permission.READ) && entity.parent.id === this.community.communityData.id ? entity : null),
                        catchError(() => of(null as HierarchyEntity))
                    );

                    const checkExistence$ = this.apiService.checkEntityExistenceByPOD(podCode.trim()).pipe(
                        map(response => response),
                        catchError(() => of({value: false} as BooleanResponse))
                    );

                    return forkJoin([findEntity$, checkExistence$]).pipe(
                        map(([entity, exists]) => {
                            if (entity) {
                                return entity;
                            } else {
                                return exists.value ? exists : null;
                            }
                        })
                    );
                } else {
                    return of(null as HierarchyEntity);
                }
            })
        );
    }

    private validatePOD(ctrl: AbstractControl): Observable<ValidationErrors> {
        const pod = mkTextField(ctrl.value);
        if (!pod) {
            return of(null);
        }
        return forkJoin([
            this.apiService.checkMemberExistenceByPOD(pod)
                .pipe(map(resp => resp.value)), // validate POD uniqueness
            this.apiService.findEntityByPOD(pod).pipe(
                switchMap(idResp => idResp && this.isThirdPartyProducer ?
                    this.apiService.getECPod(this.community.communityData.id, idResp.id).pipe(
                        map(ecPod => ecPod.plants.some(ecPlant => ecPlant.plant.capital_grant > 0))) :
                    of(false)))
        ]).pipe(map(([uniquePod, wrongPod]) => {
            if (uniquePod) {
                return {unique_pod: true};
            } else if (wrongPod) {
                return {pod_has_capital_grant: true};
            }
            return null;
        }));
    }

    private updateForm(form: FormGroup) {
        for (const ctrl in form.controls) {
            if (form.controls.hasOwnProperty(ctrl)) {
                form.controls[ctrl].updateValueAndValidity({onlySelf: true, emitEvent: true});
            }
        }
        form.updateValueAndValidity({onlySelf: true, emitEvent: true});
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (!this.mimeTypes.includes(file.type)) {
            this.msgBoxService.error(this.i18n.translate('Accepts only {0}', this.mimeTypesNames));
            return false;
        }
        return true;
    }

    private managePodAddressLikeMemberChange(podForm: FormGroup) {
        podForm.controls.podAddressLikeMember.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(
            value => {
                if (value) {
                    podForm.controls.podAddress.disable();
                    podForm.controls.podAddress.clearValidators();
                    podForm.controls.podAddress.setValue(this.currentAddress ? addressToString(this.currentAddress) : this.addressFormControl.value);
                    podForm.controls.internalPodCoordinates.setValue(null);
                    podForm.controls.internalPodAddress.setValue(null);
                } else {
                    podForm.controls.podAddress.enable();
                    podForm.controls.podAddress.setValidators([Validators.required]);
                    podForm.controls.podAddress.setValue(null);
                }
            }
        );
    }

    private getStatProfile(podForm: FormGroup): Promise<{ predictiveModel?: PredictiveModelInfo, profile?: PodStatisticalProfile } | void> {
        const energyProfile = podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
        if (energyProfile === PodEnergyProfiles.CONSUMER) {
            return this.apiService.getConsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PROSUMER) {
            return this.apiService.getProsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PRODUCER) {
            const plantsStatisticalProfiles = this.plantControls(podForm)
                .map(form => ({
                    type: getPodStatisticalProfileRequestPlantType(form.controls.plantType.value),
                    profile: form.controls.profile.value
                }));
            const req: ProducerPodStatisticalProfileRequest = {
                plants: plantsStatisticalProfiles,
                rate_mode: getRateMode(plantsStatisticalProfiles)
            };
            return this.apiService.getProducerPodStatisticalProfile(req).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(null);
                    podForm.controls.profile.setValue(response);
                    return {profile: response, predictiveModel: null};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate statistical profile for this POD'));
                });
        }
        return Promise.resolve({predictiveModel: null, profile: null});
    }

    private resetPodStatisticalProfile(podForm: FormGroup): void {
        podForm.controls.profile.setValue(null);
    }

    private validatePodDates(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        // and POD effective date(s)/deactivation date(s) validity (only if the corresponding pod is not blank)
        const effectiveDate = ctrls.podActivationDate.value !== '' ? ctrls.podActivationDate.value : null;
        const deactivationDate = ctrls.podDeactivationDate.value !== '' ? ctrls.podDeactivationDate.value : null;
        if (isDefined(effectiveDate) && isDefined(deactivationDate) && !fromBeforeTo(effectiveDate, deactivationDate)) {
            errs.pod_date_mismatch = true;
        }
        return errs;
    }

    private podCode(podForm: FormGroup): string {
        return podForm.controls.podCode.value;
    }

    private plantsFormArray(podForm: FormGroup): FormArray {
        return podForm.controls.plants as FormArray;
    }

    /**
     * Calculate plant statistical profile and predictive model on nominalPower change
     * @param plantForm
     */
    private updatePlantStatAndPredModOnChange(plantForm: FormGroup) {
        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step3Busy = true),
                debounceTime(500),
                switchMap(nominalPower => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(nominalPower, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(nominalPower, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) => {
                if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
                    plantForm.controls.profile.setValue(plantStatisticalProfile);
                } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
                    plantForm.controls.profile.reset();
                }
                plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
                this.step3Busy = false;
            });

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step3Busy = true),
                debounceTime(500),
                switchMap(() => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(plantForm.controls.nominalPower.value, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(plantForm.controls.nominalPower.value, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) => {
                if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
                    plantForm.controls.profile.setValue(plantStatisticalProfile);
                } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
                    plantForm.controls.profile.reset();
                }
                plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
                this.step3Busy = false;
            });
    }

    private mkPlantDefaultStatisticalProfile(nominalPower: number, rateMode: RateMode, type: PlantType): Observable<PlantStatisticalProfile> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantStatisticalProfile({nominal_power: nominalPower, rate_mode: rateMode});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantStatisticalProfile({
                    nominal_power: nominalPower,
                    rate_mode: rateMode,
                    zone: this.community.community.zone
                });
        }
    }

    private mkPlantDefaultPredictiveModel(nominalPower: number, type: PlantType): Observable<PredictiveModelInfo> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantPredictiveModel({});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantPredictiveModel({nominal_power: nominalPower});
        }
    }

    /**
     * search existing resource pod and link it to administrative pod
     * @private
     */
    private mkPromises() {
        return Promise.all(this.podControls.map(podForm => {
            const pod = podForm.controls.podCode.value;
            return this.apiService.findEntityByPOD(pod).toPromise().then(result => {
                if (result) {
                    this.hierarchyService.findEntityById(result.id).pipe(first()).toPromise()
                        .then(entity => podForm.controls.entityName.setValue(entity.name));
                } else {
                    if (this.isUpdate && this.isPersistentPod(podForm)) {
                        podForm.controls.entityName.setValue(podForm.controls.podCode.value);
                    } else {
                        podForm.controls.entityName.setValue(null);
                    }
                }
            });
        })).then(() => {
            this.refreshSteps3Enabled();
        });
    }

    private changeEnergyProfile() {
        if (this.isThirdPartyProducer) {
            this.podControls.forEach(podForm => {
                podForm.controls.podEnergyProfile.setValue(PodEnergyProfiles.PRODUCER);
            });
        }
    }

    private disableUtilityType() {
        this.podControls
            .filter(podForm => this.isProducerResource(podForm))
            .forEach(podForm => {
                podForm.controls.podUtilityType?.setValue('');
                podForm.controls.podUtilityType?.disable();
            });
    }

    private async createMember() {
        const sendInvitation = this.form1.controls.sendInvitation.value;
        const personalData: ECMemberPersonalData = {
            email: this.form1.controls.email.value && this.form1.controls.email.value !== '' ? this.form1.controls.email.value : null,
            company_name: this.isCompany ? this.form1.controls.companyName.value : null,
            first_name: this.form1.controls.firstName.value,
            last_name: this.form1.controls.lastName.value,
            cf: mkTextField(this.form1.controls.fiscalCode.value),
            piva: this.isCompany ? mkTextField(this.form1.controls.vatNumber.value) : null,
            pec: mkTextField(this.form1.controls.pec.value),
            iban: mkTextField(this.form1.controls.iban.value),
            address: this.currentAddress,
            mobile_number: mkTextField(this.form1.controls.mobileNumber.value),
            telephone_number: mkTextField(this.form1.controls.phoneNumber.value),
        };
        const asyncPods = this.podControls
            .map(podForm => {
                if (podForm.controls.entityName.value) {
                    return Promise.resolve(this.createEcMemberPod(podForm, null));
                } else {
                    let podName: string;
                    if (this.isCompany) {
                        podName = `${subStringForName(personalData.company_name, 6, true)} ${podForm.controls.podCode.value}`;
                    } else {
                        podName = `${subStringForName(personalData.first_name, 3, false)} ${subStringForName(personalData.last_name, 3, false)} ${podForm.controls.podCode.value}`;
                    }
                    return this.createEcMemberPodFull(podForm, podName);
                }
            });
        const pods = await Promise.all(asyncPods);

        const member: NewECMemberRequest = {
            network_id: this.community.communityData.id,
            juridical_type: this.juridicalType,
            legal_form: this.legalFormType,
            subject_type: this.subjectType,
            entity_subtype: this.entitySubtype,
            personal_data: personalData, // ECMemberPersonalData,
            spatial_coordinates: this.currentCoordinates, // SpatialCoordinates
            pods,
            participation_type: this.participationType,
            access_type: this.getAccessType
        };
        const spinner = this.openSpinner('We are creating the subject');
        try {
            const response = await this.apiService.createMember(member);
            if (sendInvitation) {
                await this.apiService.inviteMember(response.id)
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err);
                    });
            }
            if (Object.keys(this.documents).length > 0) {
                await this.uploadDocuments(response.id);
            }
            this.closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject successfully created')
            });
            spinner.closed.then(() => {
                this.dialogService.open<any, any>({
                    template: this.adviceDialog
                });
            });
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private uploadDocuments(memberId: string): Promise<(ECMemberDocument | void)[]> {
        return Promise.all(this.documentControls.map(form => {
            const fileName = form.controls.fileName.value;
            const type = form.controls.type.value;
            const customType = form.controls.customType.value;
            const id = form.controls.id.value;
            const document = this.documents[id];
            const file = {filename: fileName, content: document.file};
            // if the provided type matches one of the suggested types, use it as the type
            // otherwise, use the type inserted by the user in the custom type field
            return this.apiService.createMemberDocument(memberId, file,
                type === EC_DOCUMENT_TYPE_OTHER ? customType : type)
                .catch(err => {
                    console.error(`Cannot upload file: ${fileName}`, err);
                });
        }));
    }

    private prepareDocuments(): EditedDocument[] {
        return this.documentControls.map(docForm => {
            const docId = docForm.controls.id.value;
            const type = docForm.controls.type.value;
            const customType = docForm.controls.customType.value;
            return {
                documentId: docId,
                documentType: type === EC_DOCUMENT_TYPE_OTHER ? customType : type,
                file: this.documents[docId]?.file
            };
        });
    }

    private updateMemberDocuments(member: ECMember, editedDocs: EditedDocument[]): Promise<ECMember> {
        const newDocuments = editedDocs.filter(doc => doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX));
        const updatedDocuments = editedDocs.filter(doc => !doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX) && !isSameDocument(doc, member.documents.find(m => m.id === doc.documentId)));
        const removedDocuments = member.documents.filter(doc => !editedDocs.find(ed => ed.documentId === doc.id));
        if (!newDocuments.length && !updatedDocuments.length && !removedDocuments.length) {
            return Promise.resolve(member);
        }
        // NOTE: after all changes have been committed, the member is reloaded
        return Promise.all([
            // create new documents
            ...(newDocuments.map(doc => this.apiService.createMemberDocument(member.id, {
                content: doc.file,
                filename: doc.file.name
            }, doc.documentType, doc.metadata, doc.expiration).then(() => {
            }))),
            // update already existing documents
            ...(updatedDocuments.map(doc => this.apiService.updateMemberDocument(member.id, doc.documentId,
                doc.file ? {
                    content: doc.file,
                    filename: doc.file.name
                } : null, doc.documentType, doc.metadata, doc.expiration))),
            // delete removed documents
            ...(removedDocuments.map(doc => this.apiService.deleteMemberDocument(member.id, doc.id)))
        ]).then(() => this.apiService.loadMember(member.id).toPromise());
    }

    /**
     * createEcMemberPod for existent resources
     * @param podForm
     * @param energyCommunityNamedPod
     */
    private createEcMemberPod(podForm: FormGroup, energyCommunityNamedPod: EnergyCommunityNamedPod): ECMemberPOD {
        const podActivationDate = podForm.controls.podActivationDate.value;
        const podDeactivationDate = this.isUpdate ? podForm.controls.podDeactivationDate.value : null;
        const podAddressLikeMember = podForm.controls.podAddressLikeMember.value as boolean;
        const podAddress = podAddressLikeMember ? this.currentAddress : podForm.controls.internalPodAddress.value;
        const podCoordinates = podAddressLikeMember ? this.currentCoordinates : podForm.controls.internalPodCoordinates.value;
        const creationType = podForm.controls.creationType.value || ECPodCreationType.MANUAL;
        const aspiringProsumer = podForm.controls.aspiringProsumer.value;
        return {
            pod: podForm.controls.podCode.value,
            effective_date: podActivationDate ? podActivationDate.valueOf() : null, // number; ?
            deactivation_date: podDeactivationDate ? podDeactivationDate.valueOf() : null, // TODO add for update
            pod_utility_type: podForm.controls.podUtilityType.value, // ECPodUtilityType; ?
            address: podAddress, // ECMemberAddress; ?
            has_wifi: podForm.controls.hasWifi.value, // boolean;
            energy_provider: podForm.controls.podProvider.value, // string; ?
            spatial_coordinates: podCoordinates, // SpatialCoordinates; ?
            resource: energyCommunityNamedPod,
            creation_type: creationType,
            aspiring_prosumer: aspiringProsumer
        };
    }

    /**
     * create an object createEcMemberPod with resources
     * @param podForm
     * @param podName
     */
    private async createEcMemberPodFull(podForm: FormGroup, podName: string): Promise<ECMemberPOD> {
        const profiles = await this.getStatProfile(podForm);
        const plants: EnergyCommunityNamedPlant[] = this.plantControls(podForm)
            .map(formPlant => ({
                id: null,
                name: formPlant.controls.plantName.value,
                plant: {
                    type: EntityType.PLANT,
                    sub_type: getEntitySubtype(formPlant.controls.plantType.value),
                    voltage: podForm.controls.podVoltage.value,
                    phases: podForm.controls.podPhase.value,
                    nominal_power: formPlant.controls.nominalPower.value,
                    activation_date: formPlant.controls.enterData.value ? formPlant.controls.enterData.value.valueOf() : null,
                    incentive_mode: formPlant.controls.incentiveType.value,
                    statistical_profile: formPlant.controls.profile.value,
                    production_pod: !isBlank(formPlant.controls.podCode.value) ? formPlant.controls.podCode.value : null,
                    predictive_models: [formPlant.controls.predictiveModel.value],
                    capital_grant:
                        this.isThirdPartyProducer ||
                        isDefined(formPlant.controls.enterData.value) &&
                        (formPlant.controls.enterData.value as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone)) ?
                            0.0 :
                            formPlant.controls.capitalGrant.value / 100
                }
            }));
        const newPod: Pod = {
            type: EntityType.POD,
            voltage: podForm.controls.podVoltage.value,
            phases: podForm.controls.podPhase.value,
            producer: podForm.controls.podEnergyProfile.value === PodEnergyProfiles.PRODUCER,
            contractual_power: podForm.controls.contractualPower ? podForm.controls.contractualPower.value : null,
            predictive_models: !!profiles && !!profiles.predictiveModel ? [profiles.predictiveModel] : null,
            statistical_profile: !!profiles && !!profiles.profile ? profiles.profile : null,
            metadata: {
                [ENTITY_METADATA.POD_KEY]: this.podCode(podForm)
            }
        };
        const energyCommunityNamedPod: EnergyCommunityNamedPod = {
            id: null,
            name: podName,
            pod: newPod,
            plants
        };
        return this.createEcMemberPod(podForm, energyCommunityNamedPod);
    }

    private async updateMember(): Promise<void> {
        const sendInvitation = this.form1.controls.sendInvitation.value;
        const personalData: ECMemberPersonalData = {
            email: this.ecMember.personal_data.email ? this.ecMember.personal_data.email : this.form1.controls.email.value && this.form1.controls.email.value !== '' ? this.form1.controls.email.value : null,
            company_name: this.isCompany ? this.form1.controls.companyName.value : null,
            first_name: this.form1.controls.firstName.value,
            last_name: this.form1.controls.lastName.value,
            cf: mkTextField(this.form1.controls.fiscalCode.value),
            piva: this.isCompany ? mkTextField(this.form1.controls.vatNumber.value) : null,
            pec: mkTextField(this.form1.controls.pec.value),
            iban: mkTextField(this.form1.controls.iban.value),
            address: this.currentAddress,
            mobile_number: mkTextField(this.form1.controls.mobileNumber.value),
            telephone_number: mkTextField(this.form1.controls.phoneNumber.value)
        };
        if (this.form1.controls.entitySubtype.value !== this.ecMember.entity_subtype) {
            // If we changed the subtype, then we need to change the pod utility type of all user pods
            // to the ones that are actually supported by the new subtype
            const availableArr = getPodUtilityTypes(this.form1.controls.juridicalType.value,
                this.form1.controls.legalForm.value, this.form1.controls.subjectType.value,
                this.form1.controls.entitySubtype.value);
            if (!availableArr.includes(ECPodUtilityType.OTHER)) {  // if new subtype doesn't include other, then change it
                this.podControls.forEach(podForm => {
                    podForm.controls.podUtilityType.setValue(availableArr[0]);  // take the first one available
                });
            }
        }
        let hasNewPods = false;
        const asyncPods = this.podControls
            .map(podForm => {
                if (this.isPersistentPod(podForm)) {
                    return Promise.resolve(this.createEcMemberPod(podForm, null));
                } else {
                    hasNewPods = true;
                    if (podForm.controls.entityName.value) {
                        return Promise.resolve(this.createEcMemberPod(podForm, null));
                    } else {
                        let podName: string;
                        if (this.isCompany) {
                            podName = `${subStringForName(personalData.company_name, 6, true)} ${podForm.controls.podCode.value}`;
                        } else {
                            podName = `${subStringForName(personalData.first_name, 3, false)} ${subStringForName(personalData.last_name, 3, false)} ${podForm.controls.podCode.value}`;
                        }
                        return this.createEcMemberPodFull(podForm, podName);
                    }
                }
            });
        const pods = await Promise.all(asyncPods);

        const request: UpdateECMemberRequest = {
            u3id: this.ecMember.u3id,
            network_id: this.community.communityData.id,
            juridical_type: this.ecMember.juridical_type,
            legal_form: this.ecMember.legal_form,
            subject_type: this.ecMember.subject_type,
            entity_subtype: this.ecMember.entity_subtype || this.form1.controls.entitySubtype.value,
            personal_data: personalData, // ECMemberPersonalData,
            accepted_documents: this.ecMember.accepted_documents,
            spatial_coordinates: this.currentCoordinates, // SpatialCoordinates
            pods,
            participation_type: this.ecMember.participation_type,
            access_type: this.getAccessType
        };
        const spinner = this.openSpinner('We are updating the subject');
        try {
            await this.apiService.updateMember(this.ecMember.id, request, true);
            if (sendInvitation) {
                await this.apiService.inviteMember(this.ecMember.id)
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err);
                    });
            }

            await this.updateMemberDocuments(this.ecMember, this.prepareDocuments());

            this.closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject updated')
            });
            spinner.closed.then(() => {
                if (hasNewPods) {
                    this.dialogService.open<any, any>({
                        template: this.adviceDialog
                    });
                }
            });
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while updating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: 700,
            hideCloseButton: true
        });
    }

    private mkPlantPodValidator(): ValidatorFn {
        return ctrl => {
            const podErr = productionPODValidator(ctrl);
            if (podErr) {
                return podErr;
            }
            if (isBlank(ctrl.value)) {
                // no need to check uniqueness
                return null;
            }
            // check for production POD uniqueness
            const otherProductionPods: string[] = [];
            this.podControls.forEach(podForm => this.plantControls(podForm).forEach(plantForm => {
                if (plantForm.controls.podCode !== ctrl) {
                    otherProductionPods.push(plantForm.controls.podCode.value);
                }
            }));
            if (otherProductionPods.find(p => p?.trim() === ctrl.value)) {
                return {unique_pod: true};
            }
            return null;
        };
    }

    private setAccessTypesByEnergyProfiles() {
        const atLeastOnePodProducer = this.podControls
            .map(podForm => (podForm.controls.podEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER ||
                (podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER)
            .reduce((acc, val) => acc || val, false);
        this.setAccessTypes(atLeastOnePodProducer ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null);
    }

    private setAccessTypes(participationType?: ECMemberParticipationType) {
        if (this.featureConfig?.advanced_user && participationType && participationType === ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
            this.accessTypes = [ECMemberAccessType.ADVANCED, ECMemberAccessType.NONE];
        } else {
            this.accessTypes = this.featureConfig?.advanced_user ? ACCESS_TYPES : [ECMemberAccessType.MOBILE, ECMemberAccessType.NONE];
        }
    }
}


function validateForm(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    // company name only mandatory for `COMPANY` members
    if (!(ctrls.juridicalType.value === ECJuridicalType.INDIVIDUAL && ctrls.legalForm.value === ECLegalForm.OTHER_INDIVIDUALS)
        && !mkTextField(ctrls.companyName.value)) {
        ctrls.companyName.setErrors({empty: true});
    } else {
        ctrls.companyName.setErrors(null);
    }
    if (ctrls.juridicalType.value === ECJuridicalType.INDIVIDUAL && !ctrls.legalForm.value) {
        ctrls.legalForm.setErrors({empty: true});
    } else {
        ctrls.legalForm.setErrors(null);
    }
    if (ctrls.juridicalType.value === ECJuridicalType.CORPORATE_ENTITY &&
        (!ctrls.subjectType.value)) {
        ctrls.subjectType.setErrors({empty: true});  // there is no actual visibility of these errors aside from the asterisks (field mandatory)
    } else {
        ctrls.subjectType.setErrors(null);
    }
    if (mkTextField(ctrls.email.value) !== mkTextField(ctrls.confirmEmail.value)) {
        ctrls.confirmEmail.setErrors({not_matching_emails: true});
    } else {
        ctrls.confirmEmail.setErrors(null);
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}

function mkStatisticalProfilePodType(energyProfile: PodEnergyProfiles): StatisticalProfilePodResourceType {
    switch (energyProfile) {
        case PodEnergyProfiles.CONSUMER:
            return StatisticalProfilePodResourceType.CONSUMER;
        case PodEnergyProfiles.PRODUCER:
            return StatisticalProfilePodResourceType.PRODUCER;
        case PodEnergyProfiles.PROSUMER:
            return StatisticalProfilePodResourceType.PROSUMER;
    }
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.POD_CONNECTED_TO_NEW_ENTITY_NOT_IN_COMMUNITY:
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects resource to a member that is not part of this Energy Community';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING:
            return 'A subject with this email already exists';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING_FOR_USER:
            return 'A user with this email already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING:
            return 'A subject with this mobile number already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING_FOR_USER:
            return 'A user with this mobile number already exists';
        case ErrorCode.MISSING_REQUIRED_VALUES:
            return 'A required value is missing';
        case ErrorCode.INVALID_POD_ACTIVATION_DATE:
            return 'Invalid POD activation date';
        case ErrorCode.INVALID_FILE_TYPE:
            return 'Invalid file type';
        case ErrorCode.INVALID_MEMBER_JURIDICAL_TYPE:
            return 'Invalid energy community member juridical type provided';
        case ErrorCode.INVALID_POD_UTILITY_TYPE:
            return 'Invalid POD utility type provided';
        case ErrorCode.INVALID_DATE:
            return 'Invalid date provided';
        default:
            return fallback;
    }
}


function isSameDocument(editedDoc: EditedDocument, doc: ECMemberDocument): boolean {
    return !editedDoc.file &&
        editedDoc.documentType === doc.document_type &&
        _.isEqual(editedDoc.metadata || {}, doc.metadata || {}) &&
        sameExpiration(editedDoc.expiration, doc.expire_date);
}

function sameExpiration(exp1: number, exp2: number): boolean {
    return (isDefined(exp1) ? exp1 : null) === (isDefined(exp2) ? exp2 : null);
}

function subStringForName(from: string, requestedChars: number, isCompany: boolean): string {
    if (!isCompany) {
        const validChars = from.indexOf(' ') > 0 ? from.indexOf(' ') : from.length;
        return from.substring(0, Math.min(validChars, requestedChars));
    } else {
        let count = 0;
        let finalString = '';
        for (const currentChar of from) {
            finalString += currentChar;
            if (currentChar !== ' ' && ++count === requestedChars) {
                break;
            }
        }
        return finalString;
    }
}

function fromBeforeTo(fromDate: Moment, toDate: Moment): boolean {
    return fromDate.isBefore(toDate);
}

function isHierarchyEntity(obj: HierarchyEntity | BooleanResponse): obj is HierarchyEntity {
    return (obj as HierarchyEntity).id !== undefined;
}

function isBooleanResponse(obj: HierarchyEntity | BooleanResponse): obj is BooleanResponse {
    return (obj as BooleanResponse).value !== undefined;
}

function getProfile(pod: EnergyCommunityNamedPod): PodEnergyProfiles {
    if (pod.plants && pod.plants.length > 0) {
        if (pod.pod.producer) {
            return PodEnergyProfiles.PRODUCER;
        } else {
            return PodEnergyProfiles.PROSUMER;
        }
    }
    return PodEnergyProfiles.CONSUMER;
}

function validateDocumentType(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    // custom type only mandatory for `OTHER` type documents
    if ((ctrls.type.value === EC_DOCUMENT_TYPE_OTHER)
        && !mkTextField(ctrls.customType.value)) {
        ctrls.customType.setErrors({empty: true});
    } else {
        ctrls.customType.setErrors(null);
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}


